<template>
  <div>
    <b-row class="d-flex align-items-center justify-content-between mb-1">
      <b-col cols="12" md="6" class="d-flex align-items-center">
        <div
          class="d-flex align-items-center justify-content-center rounded"
          style="background: #e6efff"
        >
          <feather-icon icon="CreditCardIcon" size="20" style="color: #4d82e6" />
        </div>
        <h4 class="font-weight-bolder text-body ml-1 mb-0">
          Advanced Rebate Bucket (Bonus Credit) History
        </h4>
      </b-col>
      <b-col
        cols="12"
        md="6"
        class="d-flex align-items-center justify-content-md-end mt-1 mt-md-0"
        style="gap: 16px"
      >
        <div class="d-flex align-items-center" style="gap: 4px">
          <span class="text-danger">Debt:</span>
          <span class="font-weight-bolder text-danger">{{
            formatUsdt(summary.debt)
          }}</span>
        </div>
        <div class="d-flex align-items-center" style="gap: 4px">
          <span class="text-muted">Total drawn:</span>
          <span class="font-weight-bolder">{{
            formatUsdt(summary.totalDrawn)
          }}</span>
        </div>
        <div class="d-flex align-items-center" style="gap: 4px">
          <span class="text-muted">Total repaid:</span>
          <span class="font-weight-bolder">{{
            formatUsdt(summary.totalRepaid)
          }}</span>
        </div>
      </b-col>
    </b-row>

    <b-table
      :items="items"
      :fields="fields"
      responsive
      show-empty
      primary-key="id"
      thead-class="text-nowrap"
      :busy="loading"
    >
      <template #table-busy>
        <div class="text-center py-2">
          <b-spinner variant="primary" small />
        </div>
      </template>
      <template #cell(createdTime)="{ item }">
        <span class="text-nowrap">{{
          formatDateTimeLocal(item.createdTime)
        }}</span>
      </template>
      <template #cell(type)="{ item }">
        <b-badge
          :variant="item.type === 'draw' ? 'light-warning' : 'light-success'"
        >
          {{ item.type === "draw" ? "Draw" : "Repay" }}
        </b-badge>
      </template>
      <template #cell(source)="{ item }">
        <span class="text-nowrap">{{ sourceLabels[item.source] || "-" }}</span>
      </template>
      <template #cell(amount)="{ item }">
        <span class="text-nowrap">{{ formatUsdt(item.amount) }}</span>
      </template>
      <template #cell(claimedAmount)="{ item }">
        <span class="text-nowrap">{{
          item.claimedAmount === null || item.claimedAmount === undefined
            ? "-"
            : formatUsdt(item.claimedAmount)
        }}</span>
      </template>
      <template #cell(debtAfter)="{ item }">
        <span class="text-nowrap">{{ formatUsdt(item.debtAfter) }}</span>
      </template>
    </b-table>
    <div
      v-if="pagination.total > 0"
      class="d-flex align-items-center justify-content-between mt-1"
    >
      <div class="text-muted small">{{ paginationSummary }}</div>
      <b-pagination
        v-model="page"
        :total-rows="pagination.total"
        :per-page="pagination.limit"
        first-number
        last-number
        class="mb-0"
        prev-class="prev-item"
        next-class="next-item"
      >
        <template #prev-text>
          <feather-icon icon="ChevronLeftIcon" size="18" />
        </template>
        <template #next-text>
          <feather-icon icon="ChevronRightIcon" size="18" />
        </template>
      </b-pagination>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BTable,
  BBadge,
  BSpinner,
  BPagination,
} from "bootstrap-vue";
import { mapActions, mapState } from "vuex";
import { formatDateTimeLocal } from "@/helpers/utils";

const LIMIT = 20;

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BBadge,
    BSpinner,
    BPagination,
  },
  data() {
    return {
      page: 1,
      loading: false,
      items: [],
      pagination: { page: 1, limit: LIMIT, total: 0 },
      fields: [
        { key: "createdTime", label: "Time" },
        { key: "type", label: "Type" },
        { key: "source", label: "Source" },
        { key: "amount", label: "Amount" },
        { key: "claimedAmount", label: "Claimed Amount" },
        { key: "debtAfter", label: "Debt After" },
      ],
      sourceLabels: {
        "stake-cashback": "Stake Cashback",
        "net-position-rebate": "Net Position Rebate",
        "net-position-deposit-match": "Deposit Match Pool",
      },
    };
  },
  computed: {
    ...mapState({
      user: (s) => s.user.detail.userData?.user || {},
    }),
    summary() {
      return {
        debt: this.user.bonusCreditDebt || 0,
        totalDrawn: this.user.bonusCreditTotalDrawn || 0,
        totalRepaid: this.user.bonusCreditTotalRepaid || 0,
      };
    },
    paginationSummary() {
      const { page, limit, total } = this.pagination;
      const from = (page - 1) * limit + 1;
      const to = Math.min(page * limit, total);
      return `Showing ${from} - ${to} of ${total} items`;
    },
  },
  watch: {
    page() {
      this.fetchHistory();
    },
    "$route.params.id"() {
      if (this.page === 1) this.fetchHistory();
      else this.page = 1;
    },
  },
  created() {
    this.fetchHistory();
  },
  methods: {
    ...mapActions("user/detail", ["getBonusCreditHistory"]),
    formatDateTimeLocal,
    formatUsdt(value) {
      return `${Number(value || 0).toLocaleString("en-US", {
        maximumFractionDigits: 2,
      })} USDT`;
    },
    async fetchHistory() {
      this.loading = true;
      try {
        const res = await this.getBonusCreditHistory({
          userId: this.$route.params.id,
          page: this.page,
          limit: LIMIT,
        });
        if (res) {
          this.items = res.data || [];
          this.pagination = { ...this.pagination, ...res.pagination };
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
